import React from "react";
import DOMPurify from "dompurify";
import { dialog } from "../../components/ui/Dialog";
import { Icon, PrIco, ProcAv } from "../Procedures/ProcUi";
import { procFmt, procRel, procText } from "../Procedures/procHelpers";
import { procName, procWho } from "../Procedures/procPeople";
import { KB_ACCEPT, KB_DOMAINS, KB_STATUS, kbDom, kbFmtSize, kbIsEC, kbIsUrl } from "./kbHelpers";
import { televerserFichier } from "./kbApi";

export const KbBadge = ({ v }) =>
  v ? (
    <span className="kb-badge ok" title={`Validé par ${procWho(v.by).name} le ${procFmt(v.date, false)}`}>
      <Icon name="shieldCheck" size={13} /> Validé par un E-C
    </span>
  ) : (
    <span className="kb-badge no">
      <Icon name="shield" size={13} /> Non validé par un E-C
    </span>
  );

// Dossier de recherche : synthèse riche, sources, documents, validation E-C,
// statut, domaine, mots-clés, clients concernés et historique (kb.jsx).
export const KbFolder = ({ f, db, api, onBack, flash, fav, clients, nomClient, onAsk }) => {
  const d = kbDom(f.domain);
  const [editT, setEditT] = React.useState(false);
  const [title, setTitle] = React.useState(f.title);
  const [editS, setEditS] = React.useState(false);
  const [src, setSrc] = React.useState({ title: "", url: "" });
  const [srcErr, setSrcErr] = React.useState("");
  const [docErr, setDocErr] = React.useState("");
  const [tags, setTags] = React.useState(f.tags.join(", "));
  const [menu, setMenu] = React.useState(false);
  const sRef = React.useRef(null);
  const fileRef = React.useRef(null);
  const ec = kbIsEC();

  React.useEffect(() => {
    if (editS && sRef.current) {
      sRef.current.innerHTML = DOMPurify.sanitize(f.synthese || "<p></p>");
      sRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editS]);

  const docs = f.items.filter((x) => x.type === "doc").sort((a, b) => b.date - a.date);
  const srcs = f.items.filter((x) => x.type === "source").sort((a, b) => b.date - a.date);

  const ex = (c, v) => {
    sRef.current.focus();
    document.execCommand(c, false, v);
  };
  const saveS = async () => {
    const ok = await api.update(f.id, { synthese: DOMPurify.sanitize(sRef.current.innerHTML) });
    if (ok) {
      setEditS(false);
      flash("Synthèse enregistrée");
    }
  };
  const addSrc = async () => {
    const url = src.url.trim();
    if (!kbIsUrl(url)) {
      setSrcErr("Saisissez une adresse complète commençant par https://");
      return;
    }
    let host = url;
    try {
      host = new URL(url).hostname.replace(/^www\./, "");
    } catch (e) {
      // adresse déjà validée par kbIsUrl : on garde l'adresse brute comme libellé
    }
    const ok = await api.addSource(f.id, { title: src.title.trim() || host, url });
    if (ok) {
      setSrc({ title: "", url: "" });
      setSrcErr("");
    }
  };
  const addFiles = async (files) => {
    setDocErr("");
    const bad = [];
    for (const file of files) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!KB_ACCEPT[file.type] || !["pdf", "jpg", "jpeg", "png"].includes(ext)) {
        bad.push(file.name);
        continue;
      }
      if (file.size > 20 * 1048576) {
        bad.push(file.name + " (plus de 20 Mo)");
        continue;
      }
      try {
        const blob = await televerserFichier(file);
        await api.addDocument(f.id, { nom: file.name, taille: file.size, mime: file.type, url: blob.url });
      } catch (e) {
        console.error("Base de connaissance — ajout du document :", e);
        bad.push(file.name);
      }
    }
    if (bad.length) {
      setDocErr("Fichier refusé : " + bad.join(", ") + ". Seuls les formats PDF, JPEG et PNG (20 Mo maximum) sont acceptés.");
    }
  };
  const openFile = (it) => window.open(it.url, "_blank", "noopener");

  const log = db.log.filter((e) => e.fid === f.id).slice(0, 10);
  const liesIds = new Set(f.clients || []);
  const supprimerElement = async (it) => {
    const ok = await dialog.confirm("Supprimer cet élément ?", { variant: "danger", confirmLabel: "Supprimer" });
    if (ok) api.delItem(f.id, it.id);
  };
  const Del = ({ it }) => (
    <button
      className="pr-acl-rm"
      title="Supprimer"
      onClick={(e) => {
        e.stopPropagation();
        supprimerElement(it);
      }}
    >
      <Icon name="x" size={13} />
    </button>
  );
  const supprimerDossier = async () => {
    const ok = await dialog.confirm("Supprimer définitivement ce dossier et son contenu ?", {
      variant: "danger",
      confirmLabel: "Supprimer le dossier",
    });
    if (ok && (await api.remove(f.id))) {
      onBack();
      flash("Dossier supprimé");
    }
  };

  return (
    <div className="kb-page kb-folder">
      <div className="pr-crumb">
        <button onClick={onBack}>Base de connaissance</button>
        <Icon name="chev" size={12} />
        <span>{d.name}</span>
      </div>
      <div className="kb-f-head">
        <span className="kb-fold big" style={{ "--dc": d.color }}>
          <Icon name="folder" size={24} />
        </span>
        <div className="kb-f-tt">
          {editT ? (
            <input
              className="kb-f-title-in"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => {
                if (title.trim() && title !== f.title) api.update(f.id, { title: title.trim() });
                setEditT(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur();
                if (e.key === "Escape") {
                  setTitle(f.title);
                  setEditT(false);
                }
              }}
            />
          ) : (
            <h1 onClick={() => setEditT(true)} title="Cliquer pour renommer">{f.title}</h1>
          )}
          <div className="kb-f-meta">
            <KbBadge v={f.validation} />
            <span>Créé le {procFmt(f.created, false)} par {procName(f.author)}</span>
            <span>Modifié le {procFmt(f.updated, false)} par {procName(f.updatedBy || f.author)}</span>
          </div>
        </div>
        <div className="kb-f-act">
          <button
            className={`pr-ibtn ${fav ? "fav" : ""}`}
            title={fav ? "Retirer des favoris" : "Épingler en favori"}
            onClick={() => api.toggleFav(f.id)}
          >
            <PrIco n="star" s={16} />
          </button>
          <button className="btn" onClick={() => onAsk(`Résume le dossier « ${f.title} » et indique ce qu'il reste à vérifier.`)}>
            <PrIco n="spark" s={14} /> Synthèse IA
          </button>
          <div className="pr-menu-wrap">
            <button className="pr-ibtn" onClick={() => setMenu(!menu)}>
              <Icon name="dots" size={16} />
            </button>
            {menu && (
              <div className="pr-menu" onMouseLeave={() => setMenu(false)}>
                <button className="danger" onClick={supprimerDossier}>
                  <PrIco n="trash" s={14} /> Supprimer le dossier
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="kb-f-body">
        <div className="kb-f-main">
          <section className="kb-sec">
            <div className="kb-sec-h">
              <span>Synthèse</span>
              {!editS && (
                <button className="pr-link" onClick={() => setEditS(true)}>
                  <Icon name="pencil" size={12} /> Modifier
                </button>
              )}
            </div>
            {editS ? (
              <div className="kb-s-edit">
                <div className="pr-toolbar kb-s-tb">
                  {[["bold", <b key="b">B</b>], ["italic", <i key="i">I</i>]].map(([c, i]) => (
                    <button key={c} className="pr-tb-btn" onMouseDown={(e) => { e.preventDefault(); ex(c); }}>{i}</button>
                  ))}
                  <button className="pr-tb-btn" title="Liste" onMouseDown={(e) => { e.preventDefault(); ex("insertUnorderedList"); }}>
                    <PrIco n="ul" />
                  </button>
                  <button
                    className="pr-tb-btn"
                    title="Encadré à confirmer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      ex("insertHTML", `<div class="callout warn"><p><strong>À confirmer :</strong> </p></div><p><br></p>`);
                    }}
                  >
                    <PrIco n="warn" />
                  </button>
                  <button
                    className="pr-tb-btn"
                    title="Encadré conclusion"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      ex("insertHTML", `<div class="callout rule"><p class="callout-lab">Conclusion</p><p></p></div><p><br></p>`);
                    }}
                  >
                    <PrIco n="info" />
                  </button>
                </div>
                <div ref={sRef} className="pr-content kb-s-area" contentEditable suppressContentEditableWarning></div>
                <div className="kb-s-act">
                  {f.validation && (
                    <span className="kb-s-warn">
                      <Icon name="alert" size={13} /> Enregistrer lèvera la validation E-C
                    </span>
                  )}
                  <button className="btn" onClick={() => setEditS(false)}>Annuler</button>
                  <button className="btn primary" onClick={saveS}>Enregistrer</button>
                </div>
              </div>
            ) : f.synthese && procText(f.synthese) ? (
              <div className="pr-content kb-s-view" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(f.synthese) }}></div>
            ) : (
              <button className="kb-s-empty" onClick={() => setEditS(true)}>
                Rédiger la synthèse de la recherche : question posée, réponse retenue, points à confirmer.
              </button>
            )}
          </section>

          <section className="kb-sec">
            <div className="kb-sec-h">
              <span>Sources</span>
              <span className="pr-count">{srcs.length}</span>
            </div>
            <div className="kb-src-add">
              <input
                className="pr-f-in"
                value={src.title}
                onChange={(e) => setSrc({ ...src, title: e.target.value })}
                placeholder="Libellé (ex. : Article 44 quaterdecies du CGI)"
              />
              <input
                className={`pr-f-in ${srcErr ? "is-err" : ""}`}
                value={src.url}
                onChange={(e) => { setSrc({ ...src, url: e.target.value }); setSrcErr(""); }}
                placeholder="https://…"
                onKeyDown={(e) => e.key === "Enter" && addSrc()}
              />
              <button className="btn primary" disabled={!src.url.trim()} onClick={addSrc}>Ajouter</button>
            </div>
            {srcErr && <div className="kb-err">{srcErr}</div>}
            <div className="kb-srcs">
              {srcs.map((it) => (
                <div key={it.id} className="kb-src">
                  <span className="kb-it-ico"><PrIco n="link" s={15} /></span>
                  <div className="kb-it-main">
                    <a href={it.url} target="_blank" rel="noopener noreferrer" className="kb-src-t">{it.title}</a>
                    <span className="kb-src-u">{it.url}</span>
                    <span className="kb-it-meta">
                      <ProcAv id={it.author} size={16} /> {procName(it.author)} · {procFmt(it.date)}
                    </span>
                  </div>
                  <Del it={it} />
                </div>
              ))}
              {!srcs.length && (
                <div className="pr-empty">Aucune source. Ajoutez les liens consultés (Légifrance, BOFiP, jurisprudence…).</div>
              )}
            </div>
          </section>

          <section className="kb-sec">
            <div className="kb-sec-h">
              <span>Documents</span>
              <span className="pr-count">{docs.length}</span>
              <span className="kb-sec-hint">PDF, JPEG ou PNG · 20 Mo max</span>
            </div>
            <div
              className="kb-drop"
              onClick={() => fileRef.current.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles([...e.dataTransfer.files]); }}
            >
              <Icon name="download" size={16} /> Déposer des fichiers ici ou <b>parcourir</b>
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={(e) => { addFiles([...e.target.files]); e.target.value = ""; }}
              />
            </div>
            {docErr && <div className="kb-err">{docErr}</div>}
            <div className="kb-docs">
              {docs.map((it) => (
                <div key={it.id} className="kb-doc" onClick={() => openFile(it)} title="Ouvrir">
                  <span className="kb-doc-prev">
                    {/image/.test(it.mime || "") ? <img src={it.url} alt="" /> : <span className="kb-doc-ext">{(KB_ACCEPT[it.mime] || "fichier").toUpperCase()}</span>}
                  </span>
                  <span className="kb-doc-main">
                    <span className="kb-doc-n">{it.name}</span>
                    <span className="kb-it-meta">{kbFmtSize(it.size || 0)} · {procName(it.author)} · {procFmt(it.date, false)}</span>
                  </span>
                  <Del it={it} />
                </div>
              ))}
              {!docs.length && <div className="pr-empty">Aucun document.</div>}
            </div>
          </section>
        </div>

        <aside className="kb-f-side">
          <div className="kb-side-card kb-val">
            <div className="pr-rail-h">Validation</div>
            <KbBadge v={f.validation} />
            {f.validation && <p className="kb-val-t">Par {procWho(f.validation.by).name} le {procFmt(f.validation.date)}</p>}
            {!f.validation && <p className="kb-val-t">La synthèse n'a pas encore été revue par un expert-comptable.</p>}
            {ec ? (
              f.validation ? (
                <button className="btn" onClick={() => api.validate(f.id, false)}>Retirer la validation</button>
              ) : (
                <button className="btn primary" onClick={async () => { if (await api.validate(f.id, true)) flash("Dossier validé"); }}>
                  <Icon name="shieldCheck" size={14} /> Valider le dossier
                </button>
              )
            ) : (
              <p className="kb-val-t small">Seul un expert-comptable peut valider un dossier.</p>
            )}
            <p className="kb-val-t small">Toute modification de la synthèse, des sources ou des documents lève la validation.</p>
          </div>

          <div className="kb-side-card">
            <label className="pr-f-lab">Statut</label>
            <div className="pr-seg">
              {Object.entries(KB_STATUS).map(([key, s]) => (
                <button
                  key={key}
                  className={f.status === key ? "on tone-" + s.tone : ""}
                  onClick={() => f.status !== key && api.update(f.id, { status: key })}
                >
                  {s.lab}
                </button>
              ))}
            </div>
            <label className="pr-f-lab">Domaine</label>
            <select
              className="pr-f-in"
              value={f.domain}
              onChange={(e) => api.update(f.id, { domain: e.target.value })}
            >
              {KB_DOMAINS.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <label className="pr-f-lab">Mots-clés</label>
            <input
              className="pr-f-in"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              onBlur={() => {
                const t = tags.split(",").map((x) => x.trim()).filter(Boolean);
                if (t.join() !== f.tags.join()) api.update(f.id, { tags: t });
              }}
              placeholder="ZFANG, BA…"
            />
            <label className="pr-f-lab">Clients concernés</label>
            <div className="kb-cl">
              {(f.clients || []).map((id) => (
                <span key={id} className="kb-cl-i">
                  {nomClient(id) || "Client #" + id}
                  <button onClick={() => api.update(f.id, { clients: f.clients.filter((x) => x !== id) })}>
                    <Icon name="x" size={11} />
                  </button>
                </span>
              ))}
            </div>
            <select
              className="pr-f-in"
              value=""
              onChange={(e) => {
                const c = clients.find((x) => String(x.id) === e.target.value);
                if (c) api.update(f.id, { clients: [...(f.clients || []), c.id] });
              }}
            >
              <option value="">Associer un client…</option>
              {clients.filter((c) => !liesIds.has(c.id)).map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>

          <div className="kb-side-card">
            <div className="pr-rail-h">Historique</div>
            {log.map((e) => (
              <div key={e.id} className="kb-log">
                <ProcAv id={e.author} size={18} />
                <span>
                  <b>{procName(e.author)}</b>{" "}
                  {e.type === "create" ? "a créé le dossier" : (e.detail || "a modifié le dossier").replace(/^./, (c) => c.toLowerCase())}
                  <small>{procRel(e.date)}</small>
                </span>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
};
